import type {
  ImagePromptObject,
  GenerationResult,
  ImageSize,
  ImageQuality,
  Engine,
  StylePreset,
} from "../types";
import { generateImages } from "../lib/api";

interface Props {
  prompts: ImagePromptObject[];
  selectedIds: Set<string>;
  onPromptUpdate: (prompt: ImagePromptObject) => void;
  onSelectionChange: (id: string, selected: boolean) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onGenerate: (results: GenerationResult[]) => void;
  size: ImageSize;
  quality: ImageQuality;
  isGenerating: boolean;
  setIsGenerating: (v: boolean) => void;
  setError: (e: string | null) => void;
}

export function PromptReview({
  prompts,
  selectedIds,
  onPromptUpdate,
  onSelectionChange,
  onSelectAll,
  onSelectNone,
  onGenerate,
  size,
  quality,
  isGenerating,
  setIsGenerating,
  setError,
}: Props) {
  const selectedCount = selectedIds.size;

  const handleGenerate = async () => {
    if (selectedCount === 0) {
      setError("Please select at least one prompt");
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const selectedPrompts = prompts.filter((p) => selectedIds.has(p.id));
      const result = await generateImages(selectedPrompts, { size, quality });
      onGenerate(result.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate images");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Review Prompts</h2>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {selectedCount} of {prompts.length} selected
          </span>
          <button
            onClick={onSelectAll}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Select All
          </button>
          <button
            onClick={onSelectNone}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Select None
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {prompts.map((prompt) => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            isSelected={selectedIds.has(prompt.id)}
            onSelectionChange={(selected) =>
              onSelectionChange(prompt.id, selected)
            }
            onUpdate={onPromptUpdate}
          />
        ))}
      </div>

      <div className="mt-6">
        <button
          onClick={handleGenerate}
          disabled={isGenerating || selectedCount === 0}
          className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating
            ? `Generating ${selectedCount} image${selectedCount !== 1 ? "s" : ""}...`
            : `Generate ${selectedCount} Image${selectedCount !== 1 ? "s" : ""}`}
        </button>
      </div>
    </section>
  );
}

interface PromptCardProps {
  prompt: ImagePromptObject;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  onUpdate: (prompt: ImagePromptObject) => void;
}

function PromptCard({
  prompt,
  isSelected,
  onSelectionChange,
  onUpdate,
}: PromptCardProps) {
  return (
    <div
      className={`border rounded-lg p-4 ${
        isSelected ? "border-primary-500 bg-primary-50" : "border-gray-200"
      }`}
    >
      <div className="flex items-start gap-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelectionChange(e.target.checked)}
          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded"
        />

        <div className="flex-1 min-w-0">
          {/* Quote Info */}
          <div className="mb-3">
            <p className="font-medium text-gray-900">"{prompt.quoteText}"</p>
            {prompt.author && (
              <p className="text-sm text-gray-600">— {prompt.author}</p>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
              {prompt.stylePreset}
            </span>
            <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
              {prompt.engine}
            </span>
            {prompt.withText && (
              <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                with text
              </span>
            )}
            {!prompt.mockup && (
              <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                no mockup
              </span>
            )}
          </div>

          {/* Editable Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Final Prompt (editable)
            </label>
            <textarea
              value={prompt.finalPrompt}
              onChange={(e) =>
                onUpdate({ ...prompt, finalPrompt: e.target.value })
              }
              className="w-full h-32 p-3 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Quick Toggles */}
          <div className="mt-3 flex flex-wrap gap-4">
            <label className="flex items-center text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={prompt.withText}
                onChange={(e) =>
                  onUpdate({ ...prompt, withText: e.target.checked })
                }
                className="w-3 h-3 mr-1.5"
              />
              With Text
            </label>
            <label className="flex items-center text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!prompt.mockup}
                onChange={(e) =>
                  onUpdate({ ...prompt, mockup: !e.target.checked })
                }
                className="w-3 h-3 mr-1.5"
              />
              No Mockup
            </label>
            <select
              value={prompt.engine}
              onChange={(e) =>
                onUpdate({ ...prompt, engine: e.target.value as Engine })
              }
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="openai">OpenAI</option>
              <option value="workers-ai">Workers AI</option>
            </select>
            <select
              value={prompt.stylePreset}
              onChange={(e) =>
                onUpdate({
                  ...prompt,
                  stylePreset: e.target.value as StylePreset,
                })
              }
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="watercolor-travel">Watercolor Travel</option>
              <option value="fantasy-circular">Fantasy Circular</option>
              <option value="minimal-apple">Minimal Apple</option>
              <option value="fashion-ink-gold">Fashion Ink & Gold</option>
              <option value="neon-time">Neon Time</option>
              <option value="generic">Generic</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
